import { useNavigate } from 'react-router'
import type { ReactNode } from 'react'
import { Button } from '../../components/Button'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'
import { StageBar } from '../../components/StageBar'
import { WEBSITE_HEADER_OFFSET } from '../../components/WebsiteHeader'
import { useApplicationForm } from '../../features/forms/useApplicationForm'
import { useKycStep } from '../../features/forms/useKycStep'
import type { CurrentUser } from '../../hooks/useCurrentUser'
import { useI18n } from '../../i18n'
import { ApplicationStatusSection } from './ApplicationStatusSection'
import { ContactCard } from './ContactCard'
import { NextStepReminder } from './NextStepDialog'
import { FeatureCard } from './FeatureCard'
import { UserInfoHeader } from './UserInfoHeader'

const WEBSITE_CONTENT_MIN_HEIGHT = `calc(100dvh - ${WEBSITE_HEADER_OFFSET})`
const APP_CONTENT_MIN_HEIGHT =
  'calc(100dvh - (env(safe-area-inset-top, 0px) + 56px) - (64px + env(safe-area-inset-bottom, 0px)))'

function HomeLayout({
  user,
  minHeight,
  children,
}: {
  user: CurrentUser
  minHeight: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col" style={{ minHeight }}>
      <UserInfoHeader name={user.name} email={user.email} />

      <div className="flex flex-col gap-4 px-4 pt-4">
        {children}
        <ContactCard />
      </div>

      <div className="mt-auto self-end px-4 pb-4 pt-6">
        <LanguageSwitcher menuAlign="end" />
      </div>
    </div>
  )
}

function ApplyFeatureCard() {
  const { t } = useI18n()
  const navigate = useNavigate()

  return (
    <FeatureCard
      title={t('home.feature.title')}
      ctaLabel={t('home.feature.cta')}
      onAction={() => navigate('/application-of-interest')}
      imageSrc="/home-feature.jpg"
      imageAlt={t('home.feature.imageAlt')}
    />
  )
}

/**
 * Prospect variant of the home screen. Composed from presentational sections so
 * other statuses can reuse them and add their own (see HomeScreen's status seam).
 */
export function ProspectHome({ user }: { user: CurrentUser }) {
  const { t } = useI18n()
  const navigate = useNavigate()

  return (
    <HomeLayout user={user} minHeight={WEBSITE_CONTENT_MIN_HEIGHT}>
      <FeatureCard
        title={t('home.feature.title')}
        imageSrc="/home-feature.jpg"
        imageAlt={t('home.feature.imageAlt')}
        titlePosition="bottom"
      />
      <div className="flex flex-col gap-2">
        <StageBar
          prefix={t('stageBar.important.prefix')}
          label={t('stageBar.apply.label')}
        />
        <Button fullWidth onClick={() => navigate('/application-of-interest')}>
          {t('home.apply.cta')}
        </Button>
      </div>
    </HomeLayout>
  )
}

export function ApplicantHome({ user }: { user: CurrentUser }) {
  return (
    <HomeLayout user={user} minHeight={WEBSITE_CONTENT_MIN_HEIGHT}>
      <NextStepReminder step="nda" />
      <ApplicationStatusSection />
    </HomeLayout>
  )
}

/**
 * Participant home. "Complete your application" (the KYC nudge dialog + the
 * feature-card banner) only makes sense while the KYC form is still
 * actionable — once everything's submitted (`waiting`) or verified
 * (`complete`), it's misleading to keep telling the customer to complete
 * something they already did, so both are gated on the live KYC step.
 *
 * Reuses `useApplicationForm`, the same BFF call `/apply` makes (`GET
 * /me/kyc-form`) — it's the only source of the live KYC state today, so Home
 * pays for the full form schema just to read `kyc` off the response. Query
 * caching means a user who's already opened `/apply` in this session doesn't
 * refetch; a lighter kyc-only endpoint (`GET /me/kyc` — see `KycState`'s doc
 * comment in types/index.ts) would avoid the extra payload for everyone else,
 * if the BFF exposes it.
 */
export function ParticipantHome({ user }: { user: CurrentUser }) {
  const { data } = useApplicationForm()
  const { state } = useKycStep(data?.kyc)
  const kycActionable = state === 'todo' || state === 'failed'

  return (
    <HomeLayout user={user} minHeight={APP_CONTENT_MIN_HEIGHT}>
      {kycActionable && <NextStepReminder step="kyc" />}
      {kycActionable && <ApplyFeatureCard />}
      <ApplicationStatusSection />
    </HomeLayout>
  )
}
